import React from 'react';
import {observable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import {Button, Modal, Tooltip} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSync} from '@fortawesome/pro-duotone-svg-icons';

// a full guild pull is expensive server-side (~50 rosters), so refreshes share one cooldown across all pages
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;
let lastRefreshTimestamp = 0;

interface IGuildRefreshButtonProps {
    onRefresh: () => Promise<void>;
    label?: string;
    title?: string;
}

@observer
export default class GuildRefreshButton extends React.Component<IGuildRefreshButtonProps> {
    @observable private refreshing = false;
    @observable private cooldownRemainingMs = 0;
    private cooldownTimer: ReturnType<typeof setInterval> | undefined;

    componentDidMount(): void {
        this.updateCooldown();
        this.cooldownTimer = setInterval(() => this.updateCooldown(), 1000);
    }

    componentWillUnmount(): void {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer);
        }
    }

    private updateCooldown(): void {
        const remaining = Math.max(0, lastRefreshTimestamp + REFRESH_COOLDOWN_MS - Date.now());

        if (remaining !== this.cooldownRemainingMs) {
            runInAction(() => this.cooldownRemainingMs = remaining);
        }
    }

    private async onClick(): Promise<void> {
        if (this.refreshing || this.cooldownRemainingMs > 0) {
            return;
        }

        runInAction(() => this.refreshing = true);
        try {
            await this.props.onRefresh();
            lastRefreshTimestamp = Date.now();
        } catch (err: any) {
            Modal.error({
                title: "Failed to refresh guild data",
                content: <span>{err?.errorMessage ?? err?.message ?? "Unknown error"}</span>,
                maskClosable: false
            });
        } finally {
            runInAction(() => this.refreshing = false);
            this.updateCooldown();
        }
    }

    render(): React.ReactNode {
        const onCooldown = this.cooldownRemainingMs > 0;
        const cooldownMinutes = Math.ceil(this.cooldownRemainingMs / 60000);
        const tooltip = this.refreshing
            ? "Fetching fresh guild data from the game servers, this can take a while"
            : onCooldown
                ? `Guild data was refreshed recently, you can refresh again in ${cooldownMinutes} min`
                : (this.props.title ?? "Fetch fresh guild data from the game servers for this page");

        return (
            <Tooltip title={tooltip}>
                <Button onClick={() => this.onClick()} disabled={this.refreshing || onCooldown}>
                    <FontAwesomeIcon icon={faSync} spin={this.refreshing} style={{marginRight: 8}}/>
                    {this.props.label ?? "Refresh Guild"}
                </Button>
            </Tooltip>
        );
    }
}
